/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import {
  Scale, Eye, Scissors, Droplets, GitBranch,
  Info, Plus, Trash2, CheckCircle2, TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FAMACHA color scale (colors stay fixed; labels come from i18n)
const FAMACHA_COLORS = [
  { color: "#C8003E", textColor: "white" },
  { color: "#E74C5F", textColor: "white" },
  { color: "#F2A2A8", textColor: "#1C3A27" },
  { color: "#F5D5C8", textColor: "#1C3A27" },
  { color: "#F8E8D8", textColor: "#1C3A27" },
];

const TAB_DEFS = [
  { id: "weighing", icon: Scale, color: "#1E6BBA" },
  { id: "famacha", icon: Eye, color: "#C8003E" },
  { id: "hoof_trim", icon: Scissors, color: "#7A5A1C" },
  { id: "shearing", icon: Scissors, color: "#1C7A3C" },
  { id: "antiparasitic", icon: Droplets, color: "#1E6BBA" },
  { id: "lineage", icon: GitBranch, color: "#1C3A27" },
];

export default function AnimalDetailDialog({ animal, animals = [], open, onClose, onChanged }) {
  const { t } = useLang();
  const H = t.app.husbandry;
  const [tab, setTab] = useState("weighing");
  const [events, setEvents] = useState([]);
  const [, setLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(null);

  const fetchEvents = async () => {
    if (!animal) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/husbandry/${animal.id}`, { withCredentials: true });
      setEvents(data.events || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && animal) fetchEvents();
  }, [open, animal?.id]);

  if (!animal) return null;

  const eventsByType = (type) => events.filter((e) => e.type === type);

  const addEvent = async (payload) => {
    try {
      await axios.post(`${API}/husbandry`, { animal_id: animal.id, ...payload }, { withCredentials: true });
      toast.success(H.common.saved);
      await fetchEvents();
      onChanged?.();
    } catch (e) {
      toast.error(H.common.error, { description: e?.response?.data?.detail || "" });
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/husbandry/${id}`, { withCredentials: true });
      toast.success(H.common.deleted);
      await fetchEvents();
    } catch { toast.error(H.common.error); }
  };

  const isSheepOrGoat = ["Ovins", "Caprins"].includes(animal.species);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 bg-[#FAFAF7]">
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-[#1C3A27] to-[#142A1C] text-white">
          <div className="flex items-center gap-4">
            {animal.photo_url ? (
              <img src={animal.photo_url} alt={animal.tag} className="w-16 h-16 rounded-xl object-cover border-2 border-white/20" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white/10 grid place-items-center">
                <Scale size={26} className="text-white/50" />
              </div>
            )}
            <div>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-white">{animal.tag} {animal.name ? `· ${animal.name}` : ""}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-white/70 mt-1">
                {animal.species} {animal.breed ? `· ${animal.breed}` : ""} · {animal.sex === "F" ? "♀ " + (t.app.animals.female || "Femelle") : "♂ " + (t.app.animals.male || "Mâle")}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-[#E2E4DF] sticky top-0 z-10">
          <div className="flex overflow-x-auto">
            {TAB_DEFS.filter((td) => isSheepOrGoat || (td.id !== "famacha")).map((td) => {
              const Icon = td.icon;
              const active = tab === td.id;
              return (
                <button
                  key={td.id}
                  onClick={() => setTab(td.id)}
                  data-testid={`tab-${td.id}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${active ? "border-[#E05A3D] text-[#E05A3D]" : "border-transparent text-[#5C6B5C] hover:text-[#1C3A27]"}`}
                >
                  <Icon size={16} style={{ color: active ? td.color : undefined }} />
                  {H.tabs[td.id]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {tab === "weighing" && <WeighingTab H={H} events={eventsByType("weighing")} onAdd={addEvent} onDelete={deleteEvent} animal={animal} />}
          {tab === "famacha" && isSheepOrGoat && <FamachaTab H={H} events={eventsByType("famacha")} onAdd={addEvent} onDelete={deleteEvent} onInfo={() => setInfoOpen("famacha")} />}
          {tab === "hoof_trim" && <HoofTab H={H} events={eventsByType("hoof_trim")} onAdd={addEvent} onDelete={deleteEvent} onInfo={() => setInfoOpen("hoof_trim")} />}
          {tab === "shearing" && <ShearingTab H={H} events={eventsByType("shearing")} onAdd={addEvent} onDelete={deleteEvent} onInfo={() => setInfoOpen("shearing")} />}
          {tab === "antiparasitic" && <AntiparasiticTab H={H} events={eventsByType("antiparasitic")} onAdd={addEvent} onDelete={deleteEvent} onInfo={() => setInfoOpen("antiparasitic")} />}
          {tab === "lineage" && <LineageTab H={H} animal={animal} animals={animals} />}
        </div>

        {/* Info modals */}
        {infoOpen && <InfoModal kind={infoOpen} H={H} onClose={() => setInfoOpen(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function verdictFor(value, range, H, idx) {
  if (!range) return { kind: "neutral", label: "—", color: "#5C6B5C" };
  const [lo, hi] = range;
  const low = H.weighing.delta_low || "Trop faible";
  const high = H.weighing.delta_high || "Trop riche";
  const ok = H.weighing.delta_ok || "Dans la cible";
  if (value < lo) return { kind: "low",  label: low, color: "#C84B31" };
  if (value > hi) return { kind: "high", label: high, color: "#D97706" };
  return { kind: "ok", label: ok, color: "#1C7A3C" };
}

// ---------- TABS ----------

function WeighingTab({ H, events, onAdd, onDelete, animal }) {
  const [weight, setWeight] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const last = sorted[sorted.length - 1];
  const prev = sorted[sorted.length - 2];
  const delta = last && prev ? (last.weight_kg - prev.weight_kg) : null;

  // GMQ calculations
  const daysBetween = (d1, d2) => {
    const ms = new Date(d2).getTime() - new Date(d1).getTime();
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  };
  // Per-interval GMQ (between consecutive weighings)
  const gmqIntervals = sorted.slice(1).map((s, i) => {
    const p = sorted[i];
    const days = daysBetween(p.date, s.date);
    const gainKg = s.weight_kg - p.weight_kg;
    return { date: s.date, gmq_g: Math.round((gainKg * 1000) / days), days };
  });
  // Overall GMQ (first to last)
  let overallGmq = null;
  if (sorted.length >= 2) {
    const first = sorted[0];
    const lastS = sorted[sorted.length - 1];
    const days = daysBetween(first.date, lastS.date);
    overallGmq = Math.round(((lastS.weight_kg - first.weight_kg) * 1000) / days);
  }

  const submit = (e) => {
    e.preventDefault();
    if (!weight) { toast.error(H.weighing.weight_required); return; }
    onAdd({ type: "weighing", date, weight_kg: parseFloat(weight) });
    setWeight("");
  };

  const maxW = Math.max(...sorted.map((s) => s.weight_kg), 1);
  const minW = Math.min(...sorted.map((s) => s.weight_kg), maxW);
  const range = Math.max(maxW - minW, 1);

  // GMQ bar chart
  const maxGmq = Math.max(...gmqIntervals.map((g) => Math.abs(g.gmq_g)), 1);

  return (
    <div className="space-y-5" data-testid="weighing-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip label={H.weighing.stat_count} value={events.length} icon={Scale} />
        <StatChip label={H.weighing.stat_current} value={animal.weight_kg ? `${animal.weight_kg} kg` : "—"} icon={Scale} color="#1E6BBA" />
        <StatChip label={H.weighing.stat_delta} value={delta != null ? `${delta > 0 ? "+" : ""}${delta.toFixed(1)} kg` : "—"} icon={TrendingUp} color={delta > 0 ? "#1C7A3C" : delta < 0 ? "#C84B31" : "#5C6B5C"} />
        <StatChip
          label={H.weighing.stat_gmq}
          value={overallGmq != null ? `${overallGmq > 0 ? "+" : ""}${overallGmq} ${H.weighing.gmq_short}` : "—"}
          icon={TrendingUp}
          color={overallGmq > 0 ? "#E05A3D" : overallGmq < 0 ? "#C84B31" : "#5C6B5C"}
        />
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 items-end bg-white rounded-2xl border border-[#E2E4DF] p-4">
        <div>
          <Label>{H.common.date}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 bg-white" data-testid="weighing-date" />
        </div>
        <div>
          <Label>{H.weighing.form_weight}</Label>
          <Input type="number" min={0} step={0.1} value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={H.weighing.form_weight_ph} className="mt-1.5 bg-white" data-testid="weighing-weight" />
        </div>
        <Button type="submit" data-testid="weighing-add" className="bg-[#1E6BBA] hover:bg-[#155D9F] text-white">
          <Plus size={16} className="me-1" /> {H.weighing.add_btn}
        </Button>
      </form>

      {sorted.length > 1 && (
        <div className="bg-white rounded-2xl border border-[#E2E4DF] p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3">{H.weighing.chart_label}</p>
          <svg viewBox="0 0 400 100" className="w-full h-32">
            <polyline fill="none" stroke="#1E6BBA" strokeWidth="2"
              points={sorted.map((s, i) => {
                const x = (i / (sorted.length - 1)) * 380 + 10;
                const y = 90 - ((s.weight_kg - minW) / range) * 70;
                return `${x},${y}`;
              }).join(" ")} />
            {sorted.map((s, i) => {
              const x = (i / (sorted.length - 1)) * 380 + 10;
              const y = 90 - ((s.weight_kg - minW) / range) * 70;
              return <circle key={s.id} cx={x} cy={y} r="3" fill="#1E6BBA" />;
            })}
          </svg>
        </div>
      )}

      {gmqIntervals.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E2E4DF] p-4" data-testid="gmq-chart">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3">{H.weighing.gmq_chart_label}</p>
          <div className="flex items-end gap-1.5 h-24 px-1">
            {gmqIntervals.map((g, i) => {
              const h = Math.max(4, (Math.abs(g.gmq_g) / maxGmq) * 80);
              const color = g.gmq_g >= 0 ? "#E05A3D" : "#C84B31";
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative" title={`${g.date}: ${g.gmq_g > 0 ? "+" : ""}${g.gmq_g} ${H.weighing.gmq_short} (${g.days} j)`}>
                  <div className="text-[9px] font-bold" style={{ color }}>
                    {g.gmq_g > 0 ? "+" : ""}{g.gmq_g}
                  </div>
                  <div className="w-full rounded-t" style={{ height: `${h}px`, backgroundColor: color, opacity: 0.85 }} />
                  <div className="text-[9px] text-[#5C6B5C] truncate w-full text-center" style={{ fontSize: "9px" }}>
                    {g.date.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EventList H={H} events={events} onDelete={onDelete} renderRow={(e) => (
        <div className="flex items-center justify-between">
          <span className="text-[#1C3A27] font-semibold">{e.weight_kg} kg</span>
          <span className="text-xs text-[#5C6B5C]">{e.date}</span>
        </div>
      )} />
    </div>
  );
}

function FamachaTab({ H, events, onAdd, onDelete, onInfo }) {
  const [score, setScore] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const F = H.famacha;
  const levels = F.levels;

  const submit = () => {
    if (!score) { toast.error(F.choose_required); return; }
    onAdd({ type: "famacha", date, famacha_score: score, notes: notes || undefined });
    setScore(null); setNotes("");
  };

  return (
    <div className="space-y-5" data-testid="famacha-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#C8003E]/10 to-[#E74C5F]/5 border border-[#C8003E]/20 p-4 flex items-start gap-3">
        <Eye size={24} className="text-[#C8003E] flex-shrink-0 mt-1" />
        <div className="flex-1">
          <p className="font-bold text-[#1C3A27]">{F.banner_title}</p>
          <p className="text-xs text-[#5C6B5C] mt-1">{F.banner_desc}</p>
        </div>
        <button onClick={onInfo} data-testid="famacha-info-btn" className="px-3 py-2 rounded-xl bg-white border border-[#C8003E]/30 text-[#C8003E] text-xs font-bold hover:bg-[#C8003E] hover:text-white transition-colors">
          <Info size={14} className="inline me-1" /> {H.common.how_to}
        </button>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider font-bold text-[#5C6B5C]">{F.choose_label}</Label>
        <div className="mt-3 grid grid-cols-5 gap-2">
          {levels.map((lvl, idx) => {
            const c = FAMACHA_COLORS[idx];
            return (
              <button
                key={idx + 1}
                type="button"
                onClick={() => setScore(idx + 1)}
                data-testid={`famacha-${idx + 1}`}
                className={`p-3 rounded-xl border-2 transition-all ${score === idx + 1 ? "scale-105 border-[#1C3A27] shadow-lg" : "border-transparent hover:border-[#1C3A27]/30"}`}
                style={{ backgroundColor: c.color, color: c.textColor }}
              >
                <div className="font-display text-3xl font-black">{idx + 1}</div>
                <div className="text-[10px] font-bold uppercase mt-1">{lvl.label}</div>
                <div className="text-[9px] mt-0.5 opacity-90">{lvl.verdict}</div>
              </button>
            );
          })}
        </div>
        {score && (
          <div className="mt-3 rounded-xl border-2 p-3" style={{ borderColor: FAMACHA_COLORS[score - 1].color, backgroundColor: `${FAMACHA_COLORS[score - 1].color}10` }}>
            <p className="font-bold text-sm">{F.action_label}</p>
            <p className="text-sm mt-1 text-[#1C3A27]">{levels[score - 1].action}</p>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label>{H.common.date}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 bg-white" />
        </div>
        <div className="sm:col-span-2">
          <Label>{H.common.notes}</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={H.common.notes_ph} className="mt-1.5 bg-white" />
        </div>
      </div>
      <Button onClick={submit} disabled={!score} data-testid="famacha-add" className="bg-[#C8003E] hover:bg-[#A00033] text-white">
        <Plus size={16} className="me-1" /> {F.save_btn}
      </Button>

      <EventList H={H} events={events} onDelete={onDelete} renderRow={(e) => {
        const lvl = levels[e.famacha_score - 1];
        const c = FAMACHA_COLORS[e.famacha_score - 1];
        return (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full grid place-items-center font-black" style={{ backgroundColor: c?.color, color: c?.textColor }}>{e.famacha_score}</div>
            <div className="flex-1">
              <div className="font-semibold text-[#1C3A27]">{lvl?.label} — {lvl?.verdict}</div>
              <div className="text-xs text-[#5C6B5C]">{e.date}{e.notes ? ` · ${e.notes}` : ""}</div>
            </div>
          </div>
        );
      }} />
    </div>
  );
}

function HoofTab({ H, events, onAdd, onDelete, onInfo }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  return (
    <div className="space-y-5" data-testid="hoof-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#7A5A1C]/10 to-[#7A5A1C]/5 border border-[#7A5A1C]/20 p-4 flex items-start gap-3">
        <Scissors size={24} className="text-[#7A5A1C] flex-shrink-0 mt-1" />
        <div className="flex-1">
          <p className="font-bold text-[#1C3A27]">{H.hoof.banner_title}</p>
          <p className="text-xs text-[#5C6B5C] mt-1">{H.hoof.banner_desc}</p>
        </div>
        <button onClick={onInfo} data-testid="hoof-info-btn" className="px-3 py-2 rounded-xl bg-white border border-[#7A5A1C]/30 text-[#7A5A1C] text-xs font-bold hover:bg-[#7A5A1C] hover:text-white transition-colors">
          <Info size={14} className="inline me-1" /> {H.common.guide_btn}
        </button>
      </div>
      <SimpleAdd H={H} date={date} setDate={setDate} notes={notes} setNotes={setNotes} onSubmit={() => { onAdd({ type: "hoof_trim", date, notes: notes || undefined }); setNotes(""); }} color="#7A5A1C" label={H.hoof.add_btn} testid="hoof" />
      <EventList H={H} events={events} onDelete={onDelete} renderRow={(e) => (
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#1C7A3C]" />
          <span className="font-semibold text-[#1C3A27]">{H.hoof.event_label}</span>
          <span className="text-xs text-[#5C6B5C] ms-auto">{e.date}{e.notes ? ` · ${e.notes}` : ""}</span>
        </div>
      )} />
    </div>
  );
}

function ShearingTab({ H, events, onAdd, onDelete, onInfo }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [woolKg, setWoolKg] = useState("");
  const [notes, setNotes] = useState("");
  const submit = () => {
    onAdd({ type: "shearing", date, wool_kg: woolKg ? parseFloat(woolKg) : undefined, notes: notes || undefined });
    setWoolKg(""); setNotes("");
  };
  return (
    <div className="space-y-5" data-testid="shearing-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#1C7A3C]/10 to-[#1C7A3C]/5 border border-[#1C7A3C]/20 p-4 flex items-start gap-3">
        <Scissors size={24} className="text-[#1C7A3C] flex-shrink-0 mt-1" />
        <div className="flex-1">
          <p className="font-bold text-[#1C3A27]">{H.shearing.banner_title}</p>
          <p className="text-xs text-[#5C6B5C] mt-1">{H.shearing.banner_desc}</p>
        </div>
        <button onClick={onInfo} data-testid="shearing-info-btn" className="px-3 py-2 rounded-xl bg-white border border-[#1C7A3C]/30 text-[#1C7A3C] text-xs font-bold hover:bg-[#1C7A3C] hover:text-white transition-colors">
          <Info size={14} className="inline me-1" /> {H.common.guide_period}
        </button>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end bg-white rounded-2xl border border-[#E2E4DF] p-4">
        <div>
          <Label>{H.common.date}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 bg-white" />
        </div>
        <div>
          <Label>{H.shearing.form_wool}</Label>
          <Input type="number" min={0} step={0.1} value={woolKg} onChange={(e) => setWoolKg(e.target.value)} placeholder={H.shearing.form_wool_ph} className="mt-1.5 bg-white" data-testid="shearing-wool" />
        </div>
        <div className="sm:col-span-2">
          <Label>{H.common.notes}</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={H.shearing.form_notes_ph} className="mt-1.5 bg-white" />
        </div>
        <Button onClick={submit} data-testid="shearing-add" className="sm:col-span-4 bg-[#1C7A3C] hover:bg-[#155F30] text-white">
          <Plus size={16} className="me-1" /> {H.shearing.add_btn}
        </Button>
      </div>
      <EventList H={H} events={events} onDelete={onDelete} renderRow={(e) => (
        <div className="flex items-center gap-2">
          <Scissors size={16} className="text-[#1C7A3C]" />
          <span className="font-semibold text-[#1C3A27]">{H.shearing.event_label}</span>
          {e.wool_kg && <span className="text-sm text-[#1C7A3C] font-bold">{e.wool_kg} {H.shearing.wool_label}</span>}
          <span className="text-xs text-[#5C6B5C] ms-auto">{e.date}</span>
        </div>
      )} />
    </div>
  );
}

function AntiparasiticTab({ H, events, onAdd, onDelete, onInfo }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [product, setProduct] = useState("");
  const [notes, setNotes] = useState("");
  const submit = () => {
    onAdd({ type: "antiparasitic", date, product: product || undefined, notes: notes || undefined });
    setProduct(""); setNotes("");
  };
  return (
    <div className="space-y-5" data-testid="antiparasitic-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#1E6BBA]/10 to-[#1E6BBA]/5 border border-[#1E6BBA]/20 p-4 flex items-start gap-3">
        <Droplets size={24} className="text-[#1E6BBA] flex-shrink-0 mt-1" />
        <div className="flex-1">
          <p className="font-bold text-[#1C3A27]">{H.antiparasitic.banner_title}</p>
          <p className="text-xs text-[#5C6B5C] mt-1">{H.antiparasitic.banner_desc}</p>
        </div>
        <button onClick={onInfo} data-testid="antiparasitic-info-btn" className="px-3 py-2 rounded-xl bg-white border border-[#1E6BBA]/30 text-[#1E6BBA] text-xs font-bold hover:bg-[#1E6BBA] hover:text-white transition-colors">
          <Info size={14} className="inline me-1" /> {H.common.info_btn}
        </button>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end bg-white rounded-2xl border border-[#E2E4DF] p-4">
        <div>
          <Label>{H.common.date}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 bg-white" />
        </div>
        <div className="sm:col-span-2">
          <Label>{H.antiparasitic.form_product}</Label>
          <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder={H.antiparasitic.form_product_ph} className="mt-1.5 bg-white" data-testid="antiparasitic-product" />
        </div>
        <div className="sm:col-span-4">
          <Label>{H.common.notes}</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={H.antiparasitic.form_notes_ph} className="mt-1.5 bg-white" />
        </div>
        <Button onClick={submit} data-testid="antiparasitic-add" className="sm:col-span-4 bg-[#1E6BBA] hover:bg-[#155D9F] text-white">
          <Plus size={16} className="me-1" /> {H.antiparasitic.add_btn}
        </Button>
      </div>
      <EventList H={H} events={events} onDelete={onDelete} renderRow={(e) => (
        <div className="flex items-center gap-2">
          <Droplets size={16} className="text-[#1E6BBA]" />
          <span className="font-semibold text-[#1C3A27]">{e.product || H.antiparasitic.default_event}</span>
          <span className="text-xs text-[#5C6B5C] ms-auto">{e.date}{e.notes ? ` · ${e.notes}` : ""}</span>
        </div>
      )} />
    </div>
  );
}

function LineageTab({ H, animal, animals }) {
  const byId = Object.fromEntries(animals.map((a) => [a.id, a]));
  const mother = byId[animal.mother_id];
  const father = byId[animal.father_id];
  const grandMomA = mother ? byId[mother.mother_id] : null;
  const grandDadA = mother ? byId[mother.father_id] : null;
  const grandMomB = father ? byId[father.mother_id] : null;
  const grandDadB = father ? byId[father.father_id] : null;
  const children = animals.filter((a) => a.mother_id === animal.id || a.father_id === animal.id);
  const L = H.lineage;

  return (
    <div className="space-y-5" data-testid="lineage-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#1C3A27]/5 to-[#142A1C]/5 border border-[#1C3A27]/10 p-4">
        <div className="flex items-center gap-2">
          <GitBranch size={20} className="text-[#1C3A27]" />
          <p className="font-bold text-[#1C3A27]">{L.banner_title}</p>
        </div>
        <p className="text-xs text-[#5C6B5C] mt-1">{L.banner_desc}</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2E4DF] p-6 overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-4 gap-3">
            <TreeNode animal={grandMomA} label={L.grand_mother} small empty={L.empty_node} />
            <TreeNode animal={grandDadA} label={L.grand_father} small empty={L.empty_node} />
            <TreeNode animal={grandMomB} label={L.grand_mother} small empty={L.empty_node} />
            <TreeNode animal={grandDadB} label={L.grand_father} small empty={L.empty_node} />
          </div>
          <div className="grid grid-cols-4 gap-3 my-1">
            <div className="col-span-2 flex justify-center"><div className="w-px h-4 bg-[#E2E4DF]" /></div>
            <div className="col-span-2 flex justify-center"><div className="w-px h-4 bg-[#E2E4DF]" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TreeNode animal={mother} label={L.mother} accent="#E05A3D" empty={L.empty_node} />
            <TreeNode animal={father} label={L.father} accent="#1E6BBA" empty={L.empty_node} />
          </div>
          <div className="flex justify-center my-1"><div className="w-px h-4 bg-[#E2E4DF]" /></div>
          <div className="flex justify-center">
            <TreeNode animal={animal} label={L.self} accent="#1C7A3C" self empty={L.empty_node} />
          </div>
        </div>
      </div>

      {children.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E2E4DF] p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3">{L.children_label} ({children.length})</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {children.map((c) => <TreeNode key={c.id} animal={c} label={L.child} small empty={L.empty_node} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function TreeNode({ animal, label, small, self, accent = "#1C3A27", empty = "Non renseigné" }) {
  if (!animal) {
    return (
      <div className={`rounded-xl border-2 border-dashed border-[#E2E4DF] bg-[#FAFAF7] p-2 text-center ${small ? "text-xs" : "text-sm"}`}>
        <div className="text-[10px] font-bold uppercase text-[#5C6B5C]/60">{label}</div>
        <div className="text-[#5C6B5C]/40 mt-1">{empty}</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border-2 bg-white p-2 text-center" style={{ borderColor: `${accent}40`, backgroundColor: self ? `${accent}10` : "white" }}>
      <div className="text-[10px] font-bold uppercase" style={{ color: accent }}>{label}</div>
      {animal.photo_url ? (
        <img src={animal.photo_url} alt={animal.tag} className="w-12 h-12 rounded-lg object-cover mx-auto my-1" />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-[#F0F0EB] mx-auto my-1 grid place-items-center text-[#5C6B5C]/40 text-xs">{animal.tag.slice(0, 3)}</div>
      )}
      <div className="font-mono font-bold text-[#1C3A27] text-xs">{animal.tag}</div>
      {animal.name && <div className="text-[10px] text-[#5C6B5C] truncate">{animal.name}</div>}
    </div>
  );
}

function SimpleAdd({ H, date, setDate, notes, setNotes, onSubmit, color, label, testid }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3 items-end bg-white rounded-2xl border border-[#E2E4DF] p-4">
      <div>
        <Label>{H.common.date}</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 bg-white" data-testid={`${testid}-date`} />
      </div>
      <div className="sm:col-span-2">
        <Label>{H.common.notes}</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={H.common.notes_ph} className="mt-1.5 bg-white" />
      </div>
      <Button onClick={onSubmit} data-testid={`${testid}-add`} className="sm:col-span-3 text-white" style={{ backgroundColor: color }}>
        <Plus size={16} className="me-1" /> {label}
      </Button>
    </div>
  );
}

function StatChip({ label, value, icon: Icon, color = "#1C3A27" }) {
  return (
    <div className="rounded-xl bg-white border border-[#E2E4DF] p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ backgroundColor: `${color}15`, color }}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C6B5C]">{label}</div>
        <div className="font-display text-xl font-bold" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

function EventList({ H, events, onDelete, renderRow }) {
  if (events.length === 0) return <div className="text-center text-sm text-[#5C6B5C] py-6">{H.common.no_events}</div>;
  return (
    <div className="bg-white rounded-2xl border border-[#E2E4DF] divide-y divide-[#E2E4DF]">
      {events.map((e) => (
        <div key={e.id} className="flex items-center justify-between px-4 py-3" data-testid={`event-${e.id}`}>
          <div className="flex-1 min-w-0">{renderRow(e)}</div>
          <button onClick={() => { if (window.confirm(H.common.delete_confirm)) onDelete(e.id); }} className="p-2 rounded-lg hover:bg-red-50 text-red-600 ms-2">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function InfoModal({ kind, H, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        {kind === "famacha" && <FamachaGuide H={H} />}
        {kind === "hoof_trim" && <HoofGuide H={H} />}
        {kind === "shearing" && <ShearingGuide H={H} />}
        {kind === "antiparasitic" && <AntiparasiticGuide H={H} />}
      </DialogContent>
    </Dialog>
  );
}

function FamachaGuide({ H }) {
  const F = H.famacha;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl flex items-center gap-2">
          <Eye className="text-[#C8003E]" /> {F.guide_title}
        </DialogTitle>
      </DialogHeader>
      <img src="/guides/famacha_chart.png" alt="FAMACHA" className="w-full rounded-xl border border-[#E2E4DF]" onError={(e) => e.target.style.display = "none"} />
      <div>
        <p className="font-bold text-[#1C3A27]">{F.why_title}</p>
        <p className="text-sm text-[#5C6B5C] mt-1">{F.why_desc}</p>
      </div>
      <img src="/guides/famacha_check.png" alt="Examen" className="w-full rounded-xl border border-[#E2E4DF]" onError={(e) => e.target.style.display = "none"} />
      <div>
        <p className="font-bold text-[#1C3A27]">{F.how_title}</p>
        <ol className="list-decimal list-inside text-sm text-[#5C6B5C] mt-1 space-y-1">
          {F.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{F.tip}</div>
    </div>
  );
}

function HoofGuide({ H }) {
  const Hf = H.hoof;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl flex items-center gap-2">
          <Scissors className="text-[#7A5A1C]" /> {Hf.guide_title}
        </DialogTitle>
      </DialogHeader>
      <img src="/guides/hoof_trim.png" alt="Onglons" className="w-full rounded-xl border border-[#E2E4DF]" onError={(e) => e.target.style.display = "none"} />
      <div>
        <p className="font-bold text-[#1C3A27]">{Hf.steps_title}</p>
        <ol className="list-decimal list-inside text-sm text-[#5C6B5C] mt-1 space-y-1.5">
          {Hf.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>
      <img src="/guides/hoof_tools.png" alt="Outils" className="w-full rounded-xl border border-[#E2E4DF]" onError={(e) => e.target.style.display = "none"} />
      <div>
        <p className="font-bold text-[#1C3A27]">{Hf.tools_title}</p>
        <ul className="text-sm text-[#5C6B5C] mt-1 space-y-1">
          {Hf.tools.map((t) => <li key={t.name} className="flex gap-2"><CheckCircle2 size={14} className="text-[#1C7A3C] flex-shrink-0 mt-0.5" /><span><strong>{t.name}</strong> — {t.desc}</span></li>)}
        </ul>
      </div>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{Hf.warning}</div>
    </div>
  );
}

function ShearingGuide({ H }) {
  const S = H.shearing;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl flex items-center gap-2">
          <Scissors className="text-[#1C7A3C]" /> {S.guide_title}
        </DialogTitle>
      </DialogHeader>
      <img src="/guides/shearing.png" alt="Tonte" className="w-full rounded-xl border border-[#E2E4DF]" onError={(e) => e.target.style.display = "none"} />
      <div>
        <p className="font-bold text-[#1C3A27]">{S.periods_title}</p>
        <div className="mt-2 space-y-2">
          {S.periods.map((p) => (
            <div key={p.species} className="rounded-xl border border-[#E2E4DF] p-3">
              <div className="font-bold text-sm text-[#1C3A27]">{p.species}</div>
              <div className="text-[#1C7A3C] font-bold text-sm">{p.period}</div>
              <div className="text-xs text-[#5C6B5C] mt-1">{p.reason}</div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="font-bold text-[#1C3A27]">{S.tools_title}</p>
        <ul className="text-sm text-[#5C6B5C] mt-1 space-y-1">
          {S.tools.map((t) => <li key={t} className="flex gap-2"><CheckCircle2 size={14} className="text-[#1C7A3C] flex-shrink-0 mt-0.5" />{t}</li>)}
        </ul>
      </div>
      <div>
        <p className="font-bold text-[#1C3A27]">{S.steps_title}</p>
        <ol className="list-decimal list-inside text-sm text-[#5C6B5C] mt-1 space-y-1">
          {S.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>
    </div>
  );
}

function AntiparasiticGuide({ H }) {
  const A = H.antiparasitic;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl flex items-center gap-2">
          <Droplets className="text-[#1E6BBA]" /> {A.guide_title}
        </DialogTitle>
      </DialogHeader>
      <img src="/guides/antiparasitic_bath.png" alt="Bain" className="w-full rounded-xl border border-[#E2E4DF]" onError={(e) => e.target.style.display = "none"} />
      <div>
        <p className="font-bold text-[#1C3A27]">{A.why_title}</p>
        <p className="text-sm text-[#5C6B5C] mt-1">{A.why_desc}</p>
      </div>
      <div className="rounded-xl bg-[#1E6BBA]/10 border border-[#1E6BBA]/30 p-3">
        <p className="font-bold text-[#1E6BBA] text-sm">{A.frequency_title}</p>
        <p className="text-sm text-[#1C3A27] mt-1">{A.frequency_desc}</p>
      </div>
      <div>
        <p className="font-bold text-[#1C3A27]">{A.tools_title}</p>
        <ul className="text-sm text-[#5C6B5C] mt-1 space-y-1">
          {A.tools.map((t) => <li key={t} className="flex gap-2"><CheckCircle2 size={14} className="text-[#1C7A3C] flex-shrink-0 mt-0.5" />{t}</li>)}
        </ul>
      </div>
      <div>
        <p className="font-bold text-[#1C3A27]">{A.steps_title}</p>
        <ol className="list-decimal list-inside text-sm text-[#5C6B5C] mt-1 space-y-1">
          {A.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>
      <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-900">{A.warning}</div>
    </div>
  );
}
